// Shop sensors, Admin (desktop /sensors only): edit a sensor's limits / offline minutes / alerts
// on-off, send a test alert, rotate its token, register a new sensor. Behind the admin unlock the
// app uses everywhere (AdminContext + requestAdminPin); every call carries the session's Admin PIN
// (securityFetch), and a refused PIN (401 / 403) asks for it again. A token (new sensor, rotated)
// is shown ONCE, with a copy button.

import React, { useContext, useEffect, useState } from 'react';
import { AdminContext, requestAdminPin } from '../../adminSession.jsx';
import {
  createSensor,
  needsAdminPin,
  rotateSensorToken,
  sendSensorTestAlert,
  updateSensor,
} from '../../features/sensors/sensorData';
import {
  NEW_SENSOR_FORM,
  newSensorBody,
  settingsForm,
  settingsPatch,
  testAlertSummary,
} from '../../features/sensors/sensorRules';

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 10 },
  small: { fontSize: 13 },
  token: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 },
  pre: { whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 13 },
};

/** A token shown once: copy button + the warning that it won't be shown again. */
export function TokenBox({ title, token, note, onDone }) {
  const [copied, setCopied] = useState(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied('Copied.');
    } catch (_) {
      setCopied('Copy failed: select the token and copy it by hand.');
    }
  };
  return (
    <div className="alert alert-warning mb-3" role="status" data-testid="sensor-token">
      <div className="fw-bold mb-1">{title}</div>
      <div className="input-group input-group-sm mb-1">
        <input className="form-control" style={styles.token} readOnly value={token} aria-label="Sensor token"
               onFocus={(e) => e.target.select()} />
        <button type="button" className="btn btn-dark" onClick={copy}>Copy</button>
      </div>
      {copied && <div style={styles.small}>{copied}</div>}
      <div style={styles.small}>
        <strong>This token won't be shown again.</strong> Put it in the board's <code>secrets.yaml</code> now.
        {note ? ` ${note}` : ''}
      </div>
      <button type="button" className="btn btn-sm btn-outline-dark mt-2" onClick={onDone}>I've saved it</button>
    </div>
  );
}

function LimitFields({ form, set, idPrefix, busy }) {
  const field = (key, label, extra = {}) => (
    <label className="form-label mb-0" style={styles.small} htmlFor={`${idPrefix}-${key}`}>
      {label}
      <input id={`${idPrefix}-${key}`} className="form-control form-control-sm" value={form[key]}
             onChange={(e) => set(key, e.target.value)} disabled={busy} {...extra} />
    </label>
  );
  return (
    <div style={styles.grid}>
      {field('name', 'Name', { maxLength: 80 })}
      {field('location', 'Location', { maxLength: 200 })}
      {field('low_f', 'Alert below (°F, blank = off)', { inputMode: 'decimal' })}
      {field('high_f', 'Alert above (°F, blank = off)', { inputMode: 'decimal' })}
      {field('offline_minutes', 'Offline alert after (min, 5-240)', { inputMode: 'numeric' })}
    </div>
  );
}

function SensorSettings({ sensor, onSaved, onRefused }) {
  const [form, setForm] = useState(() => settingsForm(sensor));
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [token, setToken] = useState(null);

  // Another sensor picked: start from its saved values.
  useEffect(() => {
    setForm(settingsForm(sensor));
    setMsg(null);
    setToken(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensor.sensor_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const refused = (r) => {
    if (needsAdminPin(r)) {
      onRefused(r.message);
      return true;
    }
    return false;
  };

  const save = async (e) => {
    e.preventDefault();
    const { body, error } = settingsPatch(sensor, form);
    if (error) { setMsg({ kind: 'danger', text: error }); return; }
    if (!body) { setMsg({ kind: 'secondary', text: 'Nothing changed.' }); return; }
    setBusy('save');
    setMsg(null);
    const r = await updateSensor(sensor.sensor_id, body);
    setBusy(null);
    if (refused(r)) return;
    if (!r.ok) { setMsg({ kind: 'danger', text: r.message }); return; }
    setForm(settingsForm(r.body));
    setMsg({ kind: 'success', text: 'Saved. A changed limit takes effect at the next minute’s check.' });
    onSaved();
  };

  const test = async () => {
    setBusy('test');
    setMsg(null);
    const r = await sendSensorTestAlert(sensor.sensor_id);
    setBusy(null);
    if (refused(r)) return;
    if (!r.ok) { setMsg({ kind: 'danger', text: r.message }); return; }
    const errors = (r.body && r.body.errors) || [];
    const reached = ((r.body && r.body.texted) || []).length + ((r.body && r.body.pushed) || []).length;
    setMsg({ kind: errors.length && !reached ? 'warning' : 'success', text: testAlertSummary(r.body), pre: true });
    onSaved();
  };

  const rotate = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Make a new token for ${sensor.name}?\n\nThe old token stops working at once: the board can't report until it is reflashed with the new one.`)) return;
    setBusy('rotate');
    setMsg(null);
    const r = await rotateSensorToken(sensor.sensor_id);
    setBusy(null);
    if (refused(r)) return;
    if (!r.ok || !r.body || !r.body.token) { setMsg({ kind: 'danger', text: r.message || 'No token came back.' }); return; }
    setToken(r.body.token);
  };

  return (
    <div className="card mb-3" data-testid="sensor-settings">
      <div className="card-body">
        <h5 className="card-title mb-2">{sensor.name}: settings</h5>
        {token && (
          <TokenBox title={`New token for ${sensor.name}`} token={token}
                    note="The old token no longer works: reflash the board with this one." onDone={() => setToken(null)} />
        )}
        <form onSubmit={save} style={{ display: 'grid', gap: 10 }}>
          <LimitFields form={form} set={set} idPrefix={`sensor-${sensor.sensor_id}`} busy={!!busy} />
          <label className="form-check mb-0" style={styles.small}>
            <input type="checkbox" className="form-check-input" checked={form.alerts_on} disabled={!!busy}
                   onChange={(e) => set('alerts_on', e.target.checked)} />
            <span className="form-check-label">Send alerts (texts, pushes, Slack). Off: JobQ still tracks the state.</span>
          </label>
          <div className="d-flex flex-wrap gap-2">
            <button type="submit" className="btn btn-sm btn-primary" disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!!busy}
                    onClick={() => { setForm(settingsForm(sensor)); setMsg(null); }}>Undo changes</button>
            <button type="button" className="btn btn-sm btn-outline-primary ms-auto" disabled={!!busy} onClick={test}>
              {busy === 'test' ? 'Sending…' : 'Send test alert'}
            </button>
            <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!busy} onClick={rotate}>
              {busy === 'rotate' ? 'Rotating…' : 'Rotate token'}
            </button>
          </div>
        </form>
        {msg && (
          <div className={`alert alert-${msg.kind} py-1 px-2 mt-2 mb-0`} style={styles.small} role="status">
            {msg.pre ? <pre style={styles.pre}>{msg.text}</pre> : msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

function RegisterSensor({ onCreated, onRefused }) {
  const [form, setForm] = useState({ ...NEW_SENSOR_FORM });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const { body, error } = newSensorBody(form);
    if (error) { setMsg(error); return; }
    setBusy(true);
    setMsg(null);
    const r = await createSensor(body);
    setBusy(false);
    if (needsAdminPin(r)) { onRefused(r.message); return; }
    if (!r.ok || !r.body || !r.body.token) { setMsg(r.message || 'No token came back.'); return; }
    setCreated({ name: r.body.name, token: r.body.token });
    setForm({ ...NEW_SENSOR_FORM });
    onCreated(r.body.sensor_id);
  };

  return (
    <div className="card" data-testid="sensor-register">
      <div className="card-body">
        <h5 className="card-title mb-1">Register a sensor</h5>
        <p className="text-muted mb-2" style={styles.small}>
          Register the real board on jobqworks.com (the dev copy of JobQ is replaced every night, and a board
          registered there stops working).
        </p>
        {created && (
          <TokenBox title={`${created.name} is registered. Its token:`} token={created.token} onDone={() => setCreated(null)} />
        )}
        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <LimitFields form={form} set={set} idPrefix="sensor-new" busy={busy} />
          <div>
            <button type="submit" className="btn btn-sm btn-success" disabled={busy}>{busy ? 'Registering…' : 'Register sensor'}</button>
          </div>
        </form>
        {msg && <div className="alert alert-danger py-1 px-2 mt-2 mb-0" style={styles.small} role="alert">{msg}</div>}
      </div>
    </div>
  );
}

/** The Admin section. `sensor` = the one picked above (or null); onChanged() re-reads the page. */
export default function SensorAdmin({ sensor, onChanged, onCreated }) {
  const admin = useContext(AdminContext);
  const unlocked = Boolean(admin && admin.isAdminActive);
  const [pinError, setPinError] = useState(null);

  const unlock = async () => {
    const user = await requestAdminPin();
    if (!user) return;
    if (admin && admin.loginAsAdmin) {
      admin.loginAsAdmin({ userId: user.userId, userName: user.userName, role: user.role, pin: user.pin },
        { remember: user.remember });
    } else {
      sessionStorage.setItem('userRole', String(user.role || '').toLowerCase());
      sessionStorage.setItem('adminPin', user.pin);
    }
    setPinError(null);
  };

  return (
    <section className="mt-4" data-testid="sensor-admin">
      <h4 className="mb-2">Admin</h4>
      {(!unlocked || pinError) && (
        <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2">
          <span>
            {pinError
              ? `JobQ refused the Admin PIN: ${pinError}`
              : 'Settings, test alerts, tokens and new sensors need an Admin PIN.'}
          </span>
          <button type="button" className="btn btn-sm btn-dark" onClick={unlock}>Enter Admin PIN</button>
        </div>
      )}
      {unlocked && !pinError && (
        <>
          {sensor
            ? <SensorSettings sensor={sensor} onSaved={onChanged} onRefused={setPinError} />
            : <div className="text-muted mb-3">Pick a sensor above to change its settings.</div>}
          <RegisterSensor onCreated={(id) => { onChanged(); if (onCreated) onCreated(id); }} onRefused={setPinError} />
        </>
      )}
    </section>
  );
}
